import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, fetchProfileDirect } from '../lib/supabase'
import PageMeta from './PageMeta'

function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    let authSub = null

    const run = async () => {
      try {
        const hash = window.location.hash

        // ── No hash? Check for an existing session ────────────────
        if (!hash || !hash.includes('access_token')) {
          const { data: { session } } = await supabase.auth.getSession()
          if (session) {
            await handleSession(session)
            return
          }
          if (!cancelled) {
            navigate('/login', { replace: true })
          }
          return
        }

        // ── Parse tokens from hash ────────────────────────────────
        const params = new URLSearchParams(hash.substring(1))
        const access_token = params.get('access_token')
        const refresh_token = params.get('refresh_token')

        if (!access_token || !refresh_token) {
          throw new Error('Missing tokens in URL hash')
        }

        // Clean hash from address bar immediately
        window.history.replaceState(null, '', window.location.pathname)

        // ── EVENT-DRIVEN session setup ────────────────────────────
        // setSession() promise hangs in auth-js 2.70 (holds internal
        // _acquireLock) but DOES fire onAuthStateChange(SIGNED_IN).
        // So: register listener first, fire setSession without await,
        // then wait for the SIGNED_IN event.

        const sessionFromEvent = new Promise((resolve) => {
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, sess) => {
              if (event === 'SIGNED_IN' && sess) {
                subscription.unsubscribe()
                resolve(sess)
              }
            }
          )
          authSub = subscription

          // Safety: if SIGNED_IN never fires, fall back after 8 s
          setTimeout(async () => {
            subscription.unsubscribe()
            const { data } = await supabase.auth.getSession()
            resolve(data?.session || null)
          }, 8000)
        })

        // Fire-and-forget — we rely on the listener, not the promise
        supabase.auth.setSession({ access_token, refresh_token }).catch(err => {
          console.warn('⚠️ AuthCallback: setSession rejected:', err?.message)
        })

        const session = await sessionFromEvent

        if (cancelled) return

        if (!session) {
          throw new Error('Session could not be established')
        }

        await handleSession(session)

      } catch (err) {
        console.error('❌ AuthCallback:', err?.message || err)
        if (!cancelled) {
          setError('Sign-in failed. Redirecting to login...')
          setTimeout(() => !cancelled && navigate('/login', { replace: true }), 2500)
        }
      }
    }

    const handleSession = async (session) => {
      if (cancelled) return

      // Direct REST call — does NOT go through the SDK's internal lock
      const profile = await fetchProfileDirect(session.user.id)

      if (cancelled) return

      if (profile?.username && profile?.display_name) {
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin')
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin')
          navigate(redirectUrl, { replace: true })
        } else {
          navigate('/', { replace: true })
        }
      } else {
        navigate('/profile-setup', {
          state: {
            user: session.user,
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || '',
            avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || '',
            ...(profile ? { existingProfile: profile } : {}),
          },
          replace: true,
        })
      }
    }

    run()

    return () => {
      cancelled = true
      if (authSub) authSub.unsubscribe()
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark">
      <PageMeta title="Signing in | Lilycove" description="Completing sign-in to Lilycove." />
      <div className="text-center">
        {error ? (
          <>
            <p className="text-red-400 mb-2">{error}</p>
            <p className="text-cream/50 text-sm">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple mx-auto mb-4"></div>
            <p className="text-cream">Completing sign in...</p>
          </>
        )}
      </div>
    </div>
  )
}

export default AuthCallback
